import { useEffect, useMemo, useState } from "react";

import {
  Modal,
  Paper,
  TextField,
  Grid,
  Button,
  Typography,
  FormControl,
  Autocomplete,
  FormControlLabel,
  Checkbox,
  InputLabel,
  Select,
  OutlinedInput,
  Box,
  Chip,
  MenuItem,
  SelectChangeEvent,
} from "@mui/material";

import { Article, Client, Container, Group, Unit } from "types";
import { defaultEditArticleValue } from "../../../constants";
import { isValidPositiveFloatNumber } from "helpers/formatNumber";
import CustomNumberInput from "components/CustomNumberInput";
import { useSession } from "providers/SessionProvider";
import {
  getSuggestedContainers,
  getUserFullName,
  volumeCalc,
} from "helpers";

type Props = {
  open: boolean;
  handleOpen: () => void;
  value: Article | undefined;
  onEdit: (article: Article, groups: number[]) => void;
  clients: Client[];
  containers: Container[];
  units: Unit[];
  groups: Group[];
};

type ContainerOption = Pick<Container, "id" | "description"> & {
  isSuggested?: boolean;
};

export default function EditArticleModal({
  open = true,
  handleOpen,
  value = defaultEditArticleValue,
  onEdit,
  clients,
  units,
  containers,
  groups,
}: Props) {
  const { users } = useSession();
  const [data, setData] = useState<Article>(value);
  const [selectedClient, setSelectedClient] = useState(data.client);
  const [selectedUnit, setSelectedUnit] = useState(data.unit);
  const [selectedContainer, setSelectedContainer] = useState<ContainerOption>(
    data.container,
  );

  const [selectedGroups, setSelectedGroups] = useState<number[]>(
    value.GroupArticles?.map((ga) => ga.group_id) || [],
  );

  useEffect(() => {
    if (open) {
      setData(value);
      setSelectedClient(value.client);
      setSelectedUnit(value.unit);
      setSelectedContainer(value.container);
      setSelectedGroups(value.GroupArticles?.map((ga) => ga.group_id) || []);
    }
  }, [open, value, containers]);

  const groupOptions = useMemo(() => {
    return groups.filter((g) => g.client_id === value.client_id);
  }, [groups, value.client_id]);

  const articleVolume = useMemo(
    () =>
      volumeCalc(
        Number(data.width) || 0,
        Number(data.long) || 0,
        Number(data.height) || 0,
      ),
    [data.width, data.long, data.height],
  );

  const suggestedContainers = useMemo(
    () => getSuggestedContainers(containers, articleVolume),
    [containers, articleVolume],
  );

  const containerOptions = useMemo(
    () =>
      containers
        .filter((container) => container.is_active)
        .map((container) => ({
          id: container.id,
          description: container.description,
          isSuggested: suggestedContainers.some(
            (suggestedContainer) => suggestedContainer.id === container.id,
          ),
        }))
        .sort((left, right) => {
          const suggestedDiff =
            Number(Boolean(right.isSuggested)) - Number(Boolean(left.isSuggested));

          if (suggestedDiff !== 0) return suggestedDiff;

          return left.description.localeCompare(right.description);
        }),
    [containers, suggestedContainers],
  );

  const handleChange = <
    K extends keyof Omit<Article, "id" | "client" | "unit">,
  >(
    name: K,
    value: Omit<Article, "id">[K],
  ) => {
    setData({
      ...data,
      [name]: value,
    });
  };

  const handleNumberChange = <
    K extends keyof Omit<Article, "id" | "client" | "unit">,
  >(
    name: K,
    rawValue: string,
  ) => {
    setData((prev) => ({
      ...prev,
      [name]: rawValue,
    }));
  };

  const handleChangeGroups = (event: SelectChangeEvent<number[]>) => {
    const {
      target: { value },
    } = event;
    setSelectedGroups(
      typeof value === "string" ? value.split(",").map(Number) : value,
    );
  };

  return (
    <Modal open={open} onClose={handleOpen}>
      <Paper
        sx={{
          position: "absolute",
          top: "50%",
          left: "50%",
          transform: "translate(-50%, -50%)",
          width: { xs: "95%", sm: "90%", md: "40rem" },
          maxWidth: "95vw",
          maxHeight: "90vh",
          bgcolor: "background.paper",
          boxShadow: 24,
          overflowY: "auto",
          overflowX: "hidden",
          p: { xs: 2, md: 4 },
        }}
      >
        <Grid
          container
          spacing={2}
          component="form"
          onSubmit={(e: React.FormEvent<HTMLFormElement>) => {
            e.preventDefault();

            const updatedArticle: Article = {
              ...data,
              container_id: selectedContainer.id || 0,
              client_id: selectedClient.id,
              client: selectedClient,
              container: containers.find((c) => c.id === selectedContainer.id)!,
            };

            onEdit(updatedArticle, selectedGroups);
            handleOpen();
          }}
        >
          <Grid item xs={12}>
            <Typography variant="h6">Editar Producto</Typography>
          </Grid>
          <Grid item xs={6}>
            <TextField
              label="Descripción"
              fullWidth
              required
              value={data.description}
              onChange={(e) => handleChange("description", e.target.value)}
            />
          </Grid>
          <Grid item xs={6}>
            <CustomNumberInput
              label="Codigo"
              required
              value={data.code}
              disabled
              onChange={(e) => handleNumberChange("code", e.target.value)}
            />
          </Grid>
          <Grid item xs={6}>
            <CustomNumberInput
              label="Cantidad por Contenedor"
              required
              value={data.amount_per_container.toString()}
              disabled
              onChange={(e) =>
                handleNumberChange("amount_per_container", e.target.value)
              }
            />
          </Grid>
          <Grid item xs={6}>
            <CustomNumberInput
              label="Unidades por pallet"
              required
              value={data.units_per_pallet?.toString()}
              onChange={(e) =>
                handleNumberChange("units_per_pallet", e.target.value)
              }
            />
          </Grid>
          <Grid item xs={6}>
            <CustomNumberInput
              label="Peso"
              required
              value={data.weight.toString()}
              onChange={(e) => handleNumberChange("weight", e.target.value)}
            />
          </Grid>
          <Grid item xs={6}>
            <CustomNumberInput
              label="Ancho"
              value={data.width?.toString() ?? ""}
              onChange={(e) =>
                handleNumberChange("width", e.target.value)
              }
            />
          </Grid>
          <Grid item xs={6}>
            <CustomNumberInput
              label="Alto"
              value={data.height?.toString() ?? ""}
              onChange={(e) =>
                handleNumberChange("height", e.target.value)
              }
            />
          </Grid>
          <Grid item xs={6}>
            <CustomNumberInput
              label="Largo"
              value={data.long?.toString() ?? ""}
              onChange={(e) =>
                handleNumberChange("long", e.target.value)
              }
            />
          </Grid>
          <Grid item xs={6}>
            <FormControl fullWidth margin="none">
              <Autocomplete
                id="container"
                autoComplete
                value={selectedContainer}
                isOptionEqualToValue={(option, value) => option.id === value.id}
                getOptionLabel={(option) =>
                  option.isSuggested
                    ? `${option.description} • Sugerido`
                    : option.description
                }
                options={containerOptions}
                onChange={(e, value) => {
                  if (!value) return;
                  handleNumberChange("container_id", value.id.toString());
                  setSelectedContainer(value);
                }}
                fullWidth
                renderInput={(params) => (
                  <TextField {...params} required label="Contenedor" />
                )}
              />
            </FormControl>
          </Grid>
          <Grid item xs={12}>
            <Typography variant="body2" color="text.secondary">
              {articleVolume > 0
                ? suggestedContainers.length > 0
                  ? `Sugerencias por volumen (${articleVolume.toFixed(3)} m³): ${suggestedContainers
                      .map((container) => container.description)
                      .join(", ")}.`
                  : `Volumen calculado: ${articleVolume.toFixed(3)} m³. No hubo sugerencias, pero puede elegir cualquier contenedor activo.`
                : "Las medidas del artículo son opcionales. Si completa ancho, alto y largo, el sistema sugerirá contenedores posibles."}
            </Typography>
          </Grid>
          <Grid item xs={6}>
            <FormControl fullWidth margin="none">
              <Autocomplete
                id="unit"
                value={selectedUnit}
                options={units}
                getOptionLabel={(option) => option.description}
                onChange={(e, value) => {
                  if (!value) return;
                  setSelectedUnit(value);
                  setData((prevData) => ({
                    ...prevData,
                    unit_id: value.id,
                    unit: value,
                  }));
                }}
                fullWidth
                renderInput={(params) => (
                  <TextField {...params} required label="Unidad Operativa" />
                )}
              />
            </FormControl>
          </Grid>
          <Grid item xs={6}>
            <FormControl fullWidth margin="none">
              <Autocomplete
                id="client"
                value={selectedClient}
                options={clients}
                disabled
                getOptionLabel={(option) =>
                  `${option.code} - ${option.razon_social}`
                }
                onChange={(e, value) => {
                  if (!value) return;
                  setSelectedClient(value);
                  setData((prevData) => ({
                    ...prevData,
                    client_id: value.id,
                    client: value,
                  }));
                }}
                fullWidth
                renderInput={(params) => (
                  <TextField {...params} required label="Cliente" />
                )}
              />
            </FormControl>
          </Grid>
          <Grid item xs={6}>
            <TextField
              fullWidth
              label="Presentación"
              value={data.presentation ?? ""}
              onChange={(e) => handleChange("presentation", e.target.value)}
            />
          </Grid>
          <Grid item xs={6}>
            <TextField
              label="Observaciones"
              fullWidth
              minRows={2}
              maxRows={4}
              value={data.observation || ""}
              onChange={(e) => handleChange("observation", e.target.value)}
              placeholder="Observaciones"
            />
          </Grid>
          <Grid item xs={6}>
            <FormControlLabel
              control={
                <Checkbox
                  checked={data.require_fecha_vencimiento}
                  onChange={(e) =>
                    handleChange("require_fecha_vencimiento", e.target.checked)
                  }
                />
              }
              label="Requiere fecha de vencimiento"
            />
          </Grid>
          <Grid item xs={6}>
            <FormControlLabel
              control={
                <Checkbox
                  checked={data.is_standard_amount_per_container ?? true}
                  onChange={(e) =>
                    handleChange(
                      "is_standard_amount_per_container",
                      e.target.checked,
                    )
                  }
                />
              }
              label="Cantidad por contenedor estándar"
            />
          </Grid>
          <Grid item xs={6}>
            <FormControl fullWidth margin="none">
              <CustomNumberInput
                label="FCE"
                required
                value={data.fce}
                onChange={(e) => handleNumberChange("fce", e.target.value)}
              />
            </FormControl>
          </Grid>
          <Grid item xs={12}>
            <FormControl fullWidth margin="none">
              <InputLabel id="groups-multiple-label">Grupos</InputLabel>
              <Select
                labelId="groups-multiple-label"
                id="groups-multiple-chip"
                multiple
                value={selectedGroups}
                onChange={handleChangeGroups}
                input={
                  <OutlinedInput
                    id="select-groups-multiple-chip"
                    label="Grupos"
                  />
                }
                renderValue={(selected) => (
                  <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.5 }}>
                    {selected.map((value) => {
                      const group = groups.find((g) => g.id === value);
                      return <Chip key={value} label={group?.description} />;
                    })}
                  </Box>
                )}
              >
                {groupOptions.map((option) => (
                  <MenuItem key={option.id} value={option.id}>
                    {option.description}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12}>
            <TextField
              disabled
              fullWidth
              label="Creado por"
              value={
                data.created_by
                  ? getUserFullName(data.created_by, users)
                  : "SISTEMA"
              }
            />
          </Grid>
          <Grid item xs={12} display="flex" justifyContent="end" gap={1}>
            <Button
              type="button"
              variant="outlined"
              onClick={() => {
                handleOpen();
              }}
            >
              Cancelar
            </Button>
            <Button
              type="submit"
              variant="contained"
              disabled={
                !data.description ||
                !data.code ||
                !data.weight ||
                !selectedContainer ||
                !isValidPositiveFloatNumber(data.weight.toString()) ||
                !isValidPositiveFloatNumber(
                  data.amount_per_container.toString(),
                ) ||
                !data.fce
              }
            >
              Guardar
            </Button>
          </Grid>
        </Grid>
      </Paper>
    </Modal>
  );
}
